import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import api from "../lib/api";
import {
  LayoutGrid, BookUser, Package, ScrollText, FileText, ShoppingCart,
  PackageCheck, Scale, Settings, LogOut, Moon, Sun, MapPin, ArrowLeftRight,
  ClipboardCheck, ShieldAlert, ScanLine, Truck, Barcode, RefreshCw,
  AlertTriangle, ClipboardList, Gauge, History, Activity, BookOpen, TrendingDown, Landmark, TrendingUp, Rocket,
  HardHat, ClipboardEdit, Users, Building2, Receipt, Inbox, Repeat, ArrowRightLeft, Radar,
  Database, KeyRound,
} from "lucide-react";
import { ErrorBoundary } from "./ErrorBoundary";
import { InstallAppButton } from "./InstallAppButton";
import { ForcePasswordChange } from "./ForcePasswordChange";
import TopRibbon from "./TopRibbon";
import { BrandLogo } from "./BrandLogo";

function relativeAge(iso) {
  if (!iso) return "sin registro";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
}

export default function Layout() {
  const { user, logout, mustChangePassword, clearMustChangePassword } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [dark, setDark] = useState(localStorage.getItem("erp_dark") === "1");
  const [company, setCompany] = useState("MFIS Supply-Chain by OOMINING");
  const [logoMark, setLogoMark] = useState("M");
  const [logoVersion, setLogoVersion] = useState("");
  const [tagline, setTagline] = useState("by OOMINING");
  const [subtitle, setSubtitle] = useState("Mine Management Software");
  const [inboxCount, setInboxCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  const [ribbon, setRibbon] = useState(null);
  const [rbac, setRbac] = useState(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("erp_dark", dark ? "1" : "0");
  }, [dark]);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data;
      setCompany(s.company_name || "MFIS Supply-Chain by OOMINING");
      setLogoMark(s.logo_mark || "M");
      setLogoVersion(s.app_logo_version || "");
      setTagline(s.brand_tagline ?? "by OOMINING");
      setSubtitle(s.app_subtitle ?? "Mine Management Software");
      document.documentElement.style.setProperty("--primary", `${s.primary_hue} ${s.primary_sat}% ${s.primary_light}%`);
      document.documentElement.style.setProperty("--ring", `${s.primary_hue} ${s.primary_sat}% ${s.primary_light}%`);
    }).catch(() => {});
  }, []);

  // Poll ribbon + inbox counters every 60s (shared with TopRibbon via props)
  useEffect(() => {
    if (!user || user.role === "supplier") return;
    let cancelled = false;
    const load = () => {
      api.get("/system/ribbon").then((r) => !cancelled && setRibbon(r.data)).catch(() => {});
      api.get("/my/inbox").then((r) => !cancelled && setInboxCount(r.data?.totals?.total || 0)).catch(() => {});
      api.get("/notifications/unread-count").then((r) => !cancelled && setNotifCount(r.data?.unread || 0)).catch(() => {});
    };
    load();
    const h = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(h); };
  }, [user]);

  useEffect(() => {
    if (!user || user.role === "supplier" || user.role === "admin") return;
    api.get("/rbac").then((r) => setRbac(r.data.permissions?.[user.role] || null)).catch(() => {});
  }, [user]);

  const visibleModules = user?.visible_modules || [];
  const restrict = visibleModules.length > 0 && user?.role !== "admin";
  const roleCanView = (id) => !rbac || !rbac[id] || rbac[id].view !== false;
  const isVisible = (id) => id === "tutorial" || ((!restrict || visibleModules.includes(id)) && roleCanView(id));

  const rkpis = ribbon?.kpis || {};

  const groups = [
    { label: t("nav.core"), items: [
      { to: "/", icon: LayoutGrid, label: t("nav.dashboard"), id: "dashboard" },
      { to: "/address-book", icon: BookUser, label: t("nav.addressBook"), id: "address-book" },
      { to: "/items", icon: Package, label: t("nav.items"), id: "items" },
      { to: "/classifications", icon: LayoutGrid, label: t("nav.classifications"), id: "classifications" },
      { to: "/equipment", icon: Barcode, label: t("nav.equipment"), id: "equipment" },
      { to: "/cardex", icon: ScrollText, label: t("nav.cardex"), id: "cardex" },
      { to: "/stock-rotation", icon: Repeat, label: "Rotación de Existencias", id: "stock-rotation" },
    ]},
    { label: t("nav.wms"), items: [
      { to: "/locations", icon: MapPin, label: t("nav.locations"), id: "locations" },
      { to: "/transfers", icon: ArrowLeftRight, label: t("nav.transfers"), id: "transfers" },
      { to: "/vales", icon: Receipt, label: t("nav.vales"), id: "vales" },
      { to: "/cycle-counts", icon: ClipboardCheck, label: t("nav.cycleCounts"), id: "cycle-counts" },
      { to: "/quality", icon: ShieldAlert, label: t("nav.quality"), id: "quality" },
      { to: "/shipments", icon: Truck, label: t("nav.shipments"), id: "shipments" },
      { to: "/serials", icon: Barcode, label: t("nav.serials"), id: "serials" },
      { to: "/scan", icon: ScanLine, label: t("nav.scan"), id: "scan" },
    ]},
    { label: t("nav.procurement"), items: [
      { to: "/requisitions", icon: FileText, label: t("nav.requisitions"), id: "requisitions", badge: rkpis.pending_requisitions },
      { to: "/purchase-orders", icon: ShoppingCart, label: t("nav.purchaseOrders"), id: "purchase-orders", badge: rkpis.open_pos },
      { to: "/receipts", icon: PackageCheck, label: t("nav.receipts"), id: "receipts" },
      { to: "/match", icon: Scale, label: t("nav.match"), id: "match" },
      { to: "/mrp", icon: RefreshCw, label: t("nav.mrp"), id: "mrp" },
      { to: "/reabasto", icon: TrendingDown, label: t("nav.reabasto"), id: "reabasto", badge: rkpis.low_stock, badgeTone: "rose" },
      { to: "/internal-requisitions", icon: ArrowRightLeft, label: "Requisiciones Internas", id: "internal-requisitions" },
    ]},
    { label: t("nav.maintenance"), items: [
      { to: "/mnt/dashboard", icon: Activity, label: t("nav.mnt.dashboard"), id: "mnt-dashboard" },
      { to: "/mnt/dispatch", icon: Radar, label: "Dispatch Flotilla", id: "mnt-dispatch" },
      { to: "/mnt/notifications", icon: AlertTriangle, label: t("nav.mnt.notifications"), id: "mnt-notifications" },
      { to: "/mnt/pm-templates", icon: BookOpen, label: "Plantillas PM", id: "mnt-pm-templates" },
      { to: "/mnt/work-orders", icon: ClipboardList, label: t("nav.mnt.workOrders"), id: "mnt-work-orders" },
      { to: "/mnt/technicians", icon: HardHat, label: t("nav.mnt.technicians"), id: "mnt-technicians" },
      { to: "/mnt/meters", icon: Gauge, label: t("nav.mnt.meters"), id: "mnt-meters" },
      { to: "/mnt/history", icon: History, label: t("nav.mnt.history"), id: "mnt-history" },
      { to: "/mnt/failure-log", icon: ClipboardEdit, label: t("nav.mnt.failureLog"), id: "mnt-failure-log" },
      { to: "/reports/consumption-by-area", icon: TrendingDown, label: t("nav.consumptionByArea"), id: "cba" },
      { to: "/mnt/failure-codes", icon: BookOpen, label: t("nav.mnt.failures"), id: "mnt-failures" },
    ]},
    { label: t("nav.system"), items: [
      { to: "/inbox", icon: Inbox, label: t("nav.inbox"), id: "inbox", badge: inboxCount + notifCount, badgeTone: "primary" },
      { to: "/personnel", icon: Users, label: t("nav.personnel"), id: "personnel" },
      { to: "/departments", icon: Building2, label: t("nav.departments"), id: "departments" },
      { to: "/accounting", icon: Landmark, label: "Plan de Cuentas", id: "accounting" },
      { to: "/accounting/reports", icon: TrendingUp, label: "Finanzas por Contrato", id: "acc-reports" },
      { to: "/settings", icon: Settings, label: t("nav.settings"), id: "settings" },
      ...(user?.role === "admin" ? [{ to: "/roles", icon: KeyRound, label: "Roles y permisos", id: "roles" }] : []),
      { to: "/onboarding", icon: Rocket, label: "Arranque e Importación", id: "onboarding" },
      { to: "/tutorial", icon: BookOpen, label: t("nav.tutorial"), id: "tutorial" },
    ]},
  ];

  const filteredGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => isVisible(it.id)) }))
    .filter((g) => g.items.length > 0);

  const backupAge = relativeAge(ribbon?.backup?.last_at);

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <ForcePasswordChange open={!!mustChangePassword} onDone={clearMustChangePassword} />
      <TopRibbon ribbon={ribbon} notifCount={notifCount} inboxCount={inboxCount} />

      <div className="flex flex-1 min-h-0">
        <aside className="w-64 shrink-0 border-r bg-card flex flex-col fixed top-9 bottom-0 z-30" data-testid="sidebar">
          {/* Brand header — platform logo + 3 lines */}
          <div className="p-4 border-b">
            <BrandLogo version={logoVersion} className="h-14 mb-3" testid="layout-logo" />
            <div className="min-w-0" data-testid="layout-logo-mark" data-mark={logoMark}>
              <div className="font-black text-sm tracking-tight uppercase leading-none truncate text-primary"
                   data-testid="layout-brand-title">{company}</div>
              {tagline && (
                <div className="text-[10px] font-bold text-[#F29A2E] uppercase tracking-widest mt-0.5 truncate"
                     data-testid="layout-brand-tagline">{tagline}</div>
              )}
              {subtitle && (
                <div className="text-[9px] text-muted-foreground uppercase tracking-[0.15em] mt-0.5 truncate"
                     data-testid="layout-brand-subtitle">{subtitle}</div>
              )}
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto py-3">
            {filteredGroups.map((g) => (
              <div key={g.label} className="mb-3">
                <div className="px-4 py-1 text-[9px] font-bold text-muted-foreground tracking-[0.2em]">{g.label}</div>
                {g.items.map((it) => {
                  const badgeValue = it.badge;
                  const showBadge = badgeValue != null && Number(badgeValue) > 0;
                  const badgeTone = it.badgeTone || "muted";
                  const badgeClass = {
                    primary: "bg-primary text-primary-foreground",
                    rose: "bg-rose-500 text-white",
                    muted: "bg-muted text-foreground/70",
                  }[badgeTone];
                  return (
                    <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={`nav-${it.id}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-4 py-2 text-xs font-medium border-l-2 transition-colors duration-150 ${
                          isActive ? "border-primary text-primary bg-primary/5 font-bold" : "border-transparent text-foreground/70 hover:text-foreground hover:bg-muted"}`}>
                      <it.icon size={15} strokeWidth={2} />
                      <span className="flex-1">{it.label}</span>
                      {showBadge && (
                        <span className={`text-[10px] font-bold tabular-nums rounded-sm px-1.5 py-0.5 min-w-[20px] text-center ${badgeClass}`}
                              data-testid={`nav-${it.id}-badge`}>
                          {Number(badgeValue) > 99 ? "99+" : badgeValue}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Trust footer */}
          <div className="border-t p-3 space-y-2 bg-muted/30">
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground" data-testid="sidebar-footer-backup">
              <Database size={11} className="text-blue-500" />
              <span>Respaldo · {backupAge}</span>
            </div>
            <div className="pt-2 border-t border-border/40">
              <div className="text-xs font-bold truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{t(`role.${user?.role}`)} · {user?.email}</div>
              <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button"
                className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-primary hover:underline">
                <LogOut size={12} /> {t("common.logout")}
              </button>
            </div>
          </div>
        </aside>

        <div className="flex-1 ml-64 flex flex-col min-w-0">
          <header className="h-11 border-b bg-card/95 backdrop-blur flex items-center justify-end gap-2 px-6 sticky top-9 z-20">
            <InstallAppButton variant="text" />
            <div className="flex border">
              {["es", "en"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                  className={`px-2.5 py-1 text-[10px] font-bold uppercase transition-colors ${lang === l ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                  {l}
                </button>
              ))}
            </div>
            <button onClick={() => setDark(!dark)} data-testid="theme-toggle"
              className="border p-1.5 hover:bg-muted transition-colors">
              {dark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          </header>
          <main className="flex-1 p-6 lg:p-8 min-w-0 notranslate" translate="no">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}
